import React, { useState, useEffect, useCallback } from 'react';
import { Copy, Check, ExternalLink, Info } from 'lucide-react';
import BillingHistory from './billing/BillingHistory';
import useCompanyCard from '../lib/useCompanyCard';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { authService } from '../services/authService';
import { metricsService } from '../services/metricsService';
import { logHistorySettingsChange, logAudioExtractOptInChange } from '../services/uiLogger';
import { promptService } from '../services/promptService';
import { historyService } from '../services/historyService';
import { voiceService } from '../services/voiceService';
import { ROLE_LABELS } from '../lib/roles';
import { IS_SAAS } from '../config/edition';
import { formatMoscowDate } from '../lib/moscowDate';

// Личный кабинет по «Бумаге» (docs/redesign/2026-09-04-bumaga/, экран «Личный
// кабинет»): слева деньги и агенты, справа учётка и данные.
//
// 🪤 Что отсюда УБРАНО и почему это не потеря. Блок «Моя статистика» (Задач ·
// Аудио · Ошибок за 30 дней) и таблица последних задач сняты по решению
// владельца 07.09.2026: ошибок пользователю не показываем, а разрезы по дням
// живут на клиентском экране «Метрики». В админской таблице колонка «Ошибки»
// остаётся — она внутренняя.
//
// 🪤 Чего в этом экране НЕТ, хотя в макете есть: «Пополнить баланс» и «Счёт для
// юрлица». Оба живут в PricingPage и работают там; переносить платёжные кнопки
// в кабинет — решение про то, где вообще происходит оплата, а не про верстку.

const CARD = 'bg-paper-card rounded-card p-6 flex flex-col gap-4';
const LABEL = 'font-mono text-[11px] uppercase tracking-[0.1em] text-paper-ink-3';
const VALUE = 'text-[14px] text-paper-ink';
const HINT = 'text-[12px] leading-[1.5] text-paper-ink-3';
const BTN = 'rounded-[10px] px-[14px] py-[9px] text-[13px] font-semibold transition-colors';
const BTN_QUIET = `${BTN} bg-paper-bg text-paper-ink hover:bg-paper-panel`;
const BTN_DANGER = `${BTN} bg-transparent text-paper-accent hover:bg-paper-accent/[0.06]`;
const FIELD = 'w-full rounded-[10px] bg-paper-bg px-3 py-2 text-[14px] text-paper-ink '
  + 'placeholder:text-paper-hint outline-none';
const CODE = 'flex-1 min-w-0 truncate rounded-[10px] bg-paper-bg px-3 py-2 font-mono '
  + 'text-[12px] text-paper-ink select-all';

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
}

// Ссылка на документ — тот же вид, что в блоке «О сервисе».
const DOC_LINK = 'text-paper-accent underline underline-offset-2 hover:text-paper-accent-hover';

function daysLeft(dateStr) {
  if (!dateStr) return null;
  const diff = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : 0;
}

function getDayWord(n) {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (abs >= 11 && abs <= 19) return 'дней';
  if (last === 1) return 'день';
  if (last >= 2 && last <= 4) return 'дня';
  return 'дней';
}

// Кому доступны «Тарифы»: тем же ролям, что видели пункт меню до 24.09.
const PRICING_ROLES = ['client', 'admin'];

/**
 * «О сервисе»: поддержка, документы, владелец. Футера внутри приложения нет,
 * а человек ищет эти сведения в кабинете (дизайн-спринт 23.09, 3.5). Карточка
 * та же, что в подвале публичных страниц; в клиентской установке её нет, и
 * блок не рисуется.
 */
function AboutService() {
  const card = useCompanyCard();
  if (!card) return null;
  const { company, contacts, product } = card;
  const link = 'self-start text-paper-accent underline underline-offset-2 hover:text-paper-accent-hover';
  return (
    <section data-testid="about-service" className={CARD}>
      <div className="font-display text-[20px] font-extrabold tracking-[-0.03em] text-paper-ink">
        О сервисе
      </div>
      {/* Ссылки — розовым и с подчёркиванием, документы — со значком новой
          вкладки: серым текстом их не узнавали (тест владельца 25.09). */}
      <div className="flex flex-col gap-2 text-[14px]">
        <a className={link} href={`mailto:${contacts.supportEmail}`}>Поддержка: {contacts.supportEmail}</a>
        {[['/oferta', 'Публичная оферта'],
          ['/consent', 'Согласие на обработку персональных данных'],
          ['/privacy', 'Политика конфиденциальности'],
          ['/cookie', 'Политика использования cookie-файлов']].map(([href, label]) => (
          <a key={href} className={`${link} inline-flex items-center gap-1`} href={href}
             target="_blank" rel="noopener noreferrer">
            {label}
            <ExternalLink className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
          </a>
        ))}
      </div>
      <div className={`${HINT} border-t border-paper-line pt-4`}>
        {product?.name || 'Бит.Ньютон'} — сервис {company.shortName}.<br />
        ИНН {company.inn}, ОГРН {company.ogrn}.<br />
        {company.address}
        {contacts.phone && <><br />Телефон: {contacts.phone}</>}
      </div>
    </section>
  );
}

const AUDIO_EXTRACT_HINT = 'Файл меньше, загрузка быстрее; несколько секунд работы вашего '
  + 'компьютера. Распознавание от этого не меняется.';

const PLAN_TITLES = { free: 'Оплата по факту', small: 'Малый', medium: 'Средний', large: 'Большой' };

// До фазы 2 окно у всех было скользящим за 30 дней. У роли client оно стало
// биллинговым, и подпись «за последние 30 дней» превратилась в неправду ровно
// на той странице, куда человек идёт после оплаты.
function quotaWindowLabel(quota) {
  if (!quota?.period_start || !quota?.period_end) return 'за последние 30 дней';
  return `${formatDate(quota.period_start)} — ${formatDate(quota.period_end)}`;
}

// Строка расхода: слово, число и тонкая полоса. Полоса красная не бывает —
// перерасход у клиента законный и оплачивается с баланса, пугать им нечем.
function Meter({ label, used, limit, note }) {
  const unlimited = limit === null || limit === undefined;
  const pct = !unlimited && limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  return (
    <div>
      <div className="flex justify-between items-baseline">
        <span className={VALUE}>{label}</span>
        <span className="font-mono text-[13px] text-paper-ink">
          {unlimited ? 'без ограничений' : `${Math.round(used)} / ${limit}`}
        </span>
      </div>
      <div className="mt-2 h-1 rounded-[2px] bg-paper-track overflow-hidden">
        {!unlimited && <div className="h-full bg-paper-accent" style={{ width: `${pct}%` }} />}
      </div>
      {note && <div className={`${HINT} mt-2`}>{note}</div>}
    </div>
  );
}

// onOpenOrganization — переход в «Организацию»; App передаёт его только
// плательщику (кнопку «Управление организацией» искали в кабинете, а не в
// меню — тест владельца 25.09). Организацию App уже спрашивает сам.
export default function ProfilePage({ onBack, onHistoryChanged, onOpenPricing = null, onOpenMetrics = null,
                                     onOpenOrganization = null }) {
  const [profile, setProfile] = useState(null);
  const orgOwner = !!onOpenOrganization;
  const [quota, setQuota] = useState(null);
  // Из сводки нужен только офис: числа за 30 дней с экрана сняты (см. шапку).
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [displayName, setDisplayName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);

  const [copied, setCopied] = useState(null);
  const [agentsOpen, setAgentsOpen] = useState(false);
  const [showRegenerate, setShowRegenerate] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateError, setRegenerateError] = useState(null);

  const [issuedToken, setIssuedToken] = useState(null);   // показывается ОДИН раз
  const [issuing, setIssuing] = useState(false);
  const [issueError, setIssueError] = useState(null);

  const handleIssueToken = async () => {
    setIssuing(true);
    setIssueError(null);
    try {
      const { token: t } = await authService.issueApiToken();
      setIssuedToken(t);
    } catch (e) {
      setIssueError(e.message);
    } finally {
      setIssuing(false);
    }
  };

  const [histEnabled, setHistEnabled] = useState(false);
  const [histRetention, setHistRetention] = useState('month');
  const [histSaving, setHistSaving] = useState(false);
  const [retentionNote, setRetentionNote] = useState(false);
  const [confirmWipe, setConfirmWipe] = useState(false);

  const [audioExtractOptIn, setAudioExtractOptIn] = useState(true);
  const [audioExtractSaving, setAudioExtractSaving] = useState(false);

  // Голосовые отпечатки. Раздел появляется только у аккаунтов, которым функция
  // включена: голос — биометрические данные, и удалить свой человек должен
  // уметь сам, без обращения в поддержку.
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [voices, setVoices] = useState([]);
  const [voiceError, setVoiceError] = useState(null);
  const [voiceBusy, setVoiceBusy] = useState(false);

  const loadVoices = useCallback(async () => {
    try {
      setVoices(await voiceService.list());
      setVoiceError(null);
    } catch (e) {
      setVoiceError('Не удалось загрузить список голосов');
    }
  }, []);

  useEffect(() => {
    let alive = true;
    voiceService.status().then((enabled) => {
      if (!alive || !enabled) return;
      setVoiceEnabled(true);
      loadVoices();
    });
    return () => { alive = false; };
  }, [loadVoices]);

  const removeVoice = async (id) => {
    setVoiceBusy(true);
    try {
      await voiceService.remove(id);
      await loadVoices();
    } catch (e) {
      setVoiceError('Не удалось удалить голос');
    } finally {
      setVoiceBusy(false);
    }
  };

  useEffect(() => {
    promptService.getSettings()
      .then((s) => {
        setHistEnabled(!!s.history_enabled);
        if (s.history_retention) setHistRetention(s.history_retention);
        // Отсутствие значения означает включено, а не выключено (дефолт bit-auth).
        setAudioExtractOptIn(s.audio_extract_opt_in !== false);
      })
      .catch(() => {});
  }, []);

  const saveHist = async (fields) => {
    setHistSaving(true);
    if ('history_enabled' in fields) setHistEnabled(fields.history_enabled);
    if ('history_retention' in fields) { setHistRetention(fields.history_retention); setRetentionNote(true); }
    try { await promptService.updateSettings(fields); logHistorySettingsChange(fields, histRetention); onHistoryChanged?.(); } catch (_) {} finally { setHistSaving(false); }
  };

  const saveAudioExtractOptIn = async (checked) => {
    setAudioExtractSaving(true);
    setAudioExtractOptIn(checked);
    try {
      await promptService.updateSettings({ audio_extract_opt_in: checked });
      logAudioExtractOptInChange(checked);
      // audio_extract_enabled в token-данных пересчитывается на сервере и иначе
      // попал бы во фронт только при следующем /check-token (перезагрузка или
      // релогин). Перезапрашиваем сразу, чтобы выключение подействовало без
      // них — на том же вызове, которым App.js обновляет tokenData при входе.
      if (authService.isKeycloakSession()) {
        await authService.checkKeycloakSession();
      } else {
        await authService.checkToken(authService.getToken());
      }
    } catch (_) {
      // Сохранение уходит через тот же /me/settings, что и у Истории (saveHist) —
      // тот тоже не откатывает локальное состояние при ошибке.
    } finally {
      setAudioExtractSaving(false);
    }
  };

  const wipeHistory = async () => {
    setConfirmWipe(false);
    try { await historyService.removeAll(); onHistoryChanged?.(); } catch (_) {}
  };

  const loadProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await authService.getProfile();
      setProfile(data);
      setDisplayName(data.display_name || '');

      await Promise.allSettled([
        metricsService.getQuota(data.email).then(setQuota).catch(() => {}),
        metricsService.getUserSummary(data.email).then(setStats).catch(() => {}),
      ]);
    } catch (e) {
      setError(e.response?.data?.detail || 'Не удалось загрузить профиль');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleSaveDisplayName = async () => {
    setSaving(true);
    setSaveMsg(null);
    try {
      const updated = await authService.updateDisplayName(displayName);
      setProfile(updated);
      setSaveMsg({ type: 'success', text: 'Сохранено' });
      setTimeout(() => setSaveMsg(null), 3000);
    } catch (e) {
      setSaveMsg({ type: 'error', text: e.response?.data?.detail || 'Ошибка сохранения' });
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async (text, key) => {
    await navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    setRegenerateError(null);
    try {
      await authService.regenerateToken();
      authService.logout();
      window.location.reload();
    } catch (e) {
      setRegenerateError(e.response?.data?.detail || 'Ошибка при обновлении токена');
    } finally {
      setRegenerating(false);
    }
  };

  const token = authService.getToken() || '';
  const maskedToken = token.length > 6 ? token.substring(0, 6) + '••••••••••' : '••••••••••';
  // KC-сессия: опейк-токена нет → прячем токен-секции (источник правды тот же, что у getAuthHeaders).
  const isKc = authService.isKeycloakSession();
  const CLI_INSTALL = 'curl -sL https://gitlab.com/fadeyev1/newton-cli/-/raw/main/newton'
    + ' -o ~/.local/bin/newton && chmod +x ~/.local/bin/newton';

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-paper-accent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-panel bg-paper-accent/[0.06] text-paper-accent p-4 text-[14px]">{error}</div>
      </div>
    );
  }

  const days = daysLeft(profile.token_expires);
  const copyBtn = (key) => (
    <span className="shrink-0">
      {copied === key
        ? <Check className="w-4 h-4 text-paper-success" />
        : <Copy className="w-4 h-4 text-paper-ink-3" />}
    </span>
  );

  return (
    <div>
      {/* Своего заголовка и «Назад» нет: название уже в верхней строке, как на
          остальных экранах, а в кабинет и обратно ходят через меню (замечание
          владельца 24.09.2026 — строка повторяла заголовок). */}

      <div className="grid gap-3 grid-cols-[minmax(0,1fr)] lg:grid-cols-[minmax(0,1fr)_332px] items-start">
        {/* ── Левая колонка: деньги и агенты ─────────────────────────── */}
        <div className="flex flex-col gap-3">
          <section className={CARD}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <div>
                <div className="font-display text-[28px] font-extrabold tracking-[-0.03em] text-paper-ink">
                  {PLAN_TITLES[quota?.plan_code] || 'Тариф не назначен'}
                </div>
                <div className={HINT}>
                  {quota?.plan_code === 'free'
                    ? 'Пакет минут в подарок, дальше — с баланса. Деньги на балансе не сгорают в конце месяца.'
                    : 'Платный тариф'}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className={`${LABEL} text-right`}>{quotaWindowLabel(quota)}</div>
                {/* «Тарифы» ушли из меню сюда (дизайн-спринт 23.09, 3.4 и 3.8):
                    менять тариф человек ищет там, где его видит. Сотруднику
                    тарифов нет, как не было и пункта меню. */}
                {onOpenPricing && PRICING_ROLES.includes(profile.role) && (
                  <button type="button" data-testid="change-plan" className={BTN_QUIET} onClick={onOpenPricing}>
                    Сменить тариф
                  </button>
                )}
                {orgOwner && (
                  <button type="button" data-testid="manage-org" className={BTN_QUIET} onClick={onOpenOrganization}>
                    Управление организацией
                  </button>
                )}
              </div>
            </div>

            {quota ? (
              <div className="flex flex-col gap-4 pt-1">
                <Meter label="Минуты расшифровки" used={quota.audio.used} limit={quota.audio.limit} />
                <Meter label="Протоколы" used={quota.summary.used} limit={quota.summary.limit}
                       note="Вопросы к готовому протоколу в чате лимит не тратят." />
                {/* «Метрики» ушли из навигации в меню пользователя (10.10.2026) —
                    отсюда, от расхода, к ним самый естественный вход. */}
                {onOpenMetrics && (
                  <button type="button" data-testid="open-metrics" className={`${BTN_QUIET} self-start`} onClick={onOpenMetrics}>
                    Подробнее в Метриках
                  </button>
                )}
                {quota.overage && (
                  <div className="rounded-panel bg-paper-panel p-4">
                    <div className={VALUE}>
                      Сверх пакета:{' '}
                      <b className="font-mono font-medium">{quota.overage_minutes} мин</b>
                    </div>
                    <div className={`${HINT} mt-1`}>Спишется с баланса в конце периода.</div>
                  </div>
                )}
              </div>
            ) : (
              <p className={HINT}>Не удалось загрузить данные о квотах</p>
            )}
          </section>

          {/* Платежи и списания — только у внешнего клиента: у сотрудника их не
              бывает, а карточка стоила бы двух лишних запросов на каждый заход. */}
          {profile?.role === 'client' && <BillingHistory />}

          {/* Свёрнуто по умолчанию: токены и команды терминала человек, пришедший
              за протоколом, принимал за «выбор моделей» и пугался (прогон
              организаций 24.09). Кому нужно — раскроет. */}
          <section className={CARD}>
            <button type="button" onClick={() => setAgentsOpen((v) => !v)}
                    aria-expanded={agentsOpen} className="text-left">
              <div className="flex items-center justify-between gap-3 font-display text-[20px] font-extrabold tracking-[-0.03em] text-paper-ink">
                Как подключить Ньютон к вашему агенту
                <span className="text-[14px] font-normal text-paper-ink-3">{agentsOpen ? 'Свернуть' : 'Показать'}</span>
              </div>
              <div className={`${HINT} mt-1`}>
                Newton можно вызывать из терминала или из AI-агентов — Claude Code, Cursor, Codex,
                OpenCode, Hermes.
              </div>
            </button>
            {agentsOpen && (<>

            <div>
              <div className={LABEL}>Установка CLI</div>
              <button type="button" onClick={() => handleCopy(CLI_INSTALL, 'cli')}
                      className="mt-2 w-full flex items-center gap-2 text-left">
                <code className={CODE}>{CLI_INSTALL}</code>
                {copyBtn('cli')}
              </button>
            </div>

            {isKc ? (
              <div>
                <div className={LABEL}>Токен для интеграций</div>
                <div className={`${HINT} mt-1`}>
                  Долгоживущий, продлевается при использовании. Покажем один раз.
                </div>
                {issuedToken ? (
                  <div className="mt-2 flex flex-col gap-2">
                    <button type="button" className="w-full flex items-center gap-2 text-left"
                            onClick={() => handleCopy(`export NEWTON_TOKEN=${issuedToken}`, 'export')}>
                      <code className={CODE}>export NEWTON_TOKEN={issuedToken}</code>
                      {copyBtn('export')}
                    </button>
                    <p className="text-[12px] text-paper-warning">
                      Сохраните сейчас — больше не покажем. Перевыпуск отзовёт этот токен.
                    </p>
                  </div>
                ) : (
                  <div className="mt-2">
                    <button type="button" className={BTN_QUIET} disabled={issuing} onClick={handleIssueToken}>
                      {issuing ? 'Выпуск…' : 'Выпустить токен для интеграций'}
                    </button>
                    {issueError && <p className="text-[12px] text-paper-accent mt-2">{issueError}</p>}
                  </div>
                )}
              </div>
            ) : (
              <div>
                <div className={LABEL}>Токен для CLI</div>
                <button type="button" className="mt-2 w-full flex items-center gap-2 text-left"
                        onClick={() => handleCopy(`export NEWTON_TOKEN=${token}`, 'export')}>
                  <code className={CODE}>export NEWTON_TOKEN={maskedToken}</code>
                  {copyBtn('export')}
                </button>
              </div>
            )}

            <div className="flex items-center gap-5 text-[13px]">
              <a href="https://gitlab.com/fadeyev1/newton-cli/-/raw/main/newton-cli.md"
                 target="_blank" rel="noopener noreferrer"
                 className="flex items-center gap-1 font-semibold text-paper-ink hover:text-paper-accent">
                <ExternalLink className="w-3.5 h-3.5" /> Скил для AI-агентов
              </a>
              <a href="https://gitlab.com/fadeyev1/newton-cli"
                 target="_blank" rel="noopener noreferrer"
                 className="flex items-center gap-1 text-paper-ink-3 hover:text-paper-ink">
                <ExternalLink className="w-3.5 h-3.5" /> Репозиторий
              </a>
            </div>

            {!isKc && (
              <div className="flex flex-col gap-3">
                <div>
                  <div className={LABEL}>Токен доступа</div>
                  <button type="button" className="mt-2 w-full flex items-center gap-2 text-left"
                          onClick={() => handleCopy(token, 'token')}>
                    <code className={CODE}>{maskedToken}</code>
                    {copyBtn('token')}
                  </button>
                </div>
                <div>
                  <div className={LABEL}>Действителен до</div>
                  <div className={`${VALUE} mt-1`}>
                    {formatDate(profile.token_expires)}
                    {days !== null && (
                      <span className={`ml-2 text-[12px] ${days > 7 ? 'text-paper-ink-3' : 'text-paper-accent'}`}>
                        осталось {days} {getDayWord(days)}
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <button type="button" className={BTN_DANGER} onClick={() => setShowRegenerate(true)}>
                    Обновить токен
                  </button>
                  <p className={`${HINT} mt-2`}>
                    Новый токен придёт на почту. Текущий станет недействительным.
                  </p>
                </div>
              </div>
            )}
            </>)}
          </section>
        </div>

        {/* ── Правая колонка: учётка и данные ────────────────────────── */}
        <div className="flex flex-col gap-3">
          <section className={CARD}>
            <div>
              <div className={`${VALUE} font-medium break-all`}>{profile.email}</div>
              <div className={`${HINT} mt-1`}>
                {ROLE_LABELS[profile.role] || profile.role} · с {formatDate(profile.created_at)}
                {stats?.office && ` · ${stats.office}`}
              </div>
              {/* Когда человек принял оферту и согласие (журнал bit-auth, GET /me → legal).
                  Не принял — строки нет: после входа его спросит окно «Обновились документы». */}
              {IS_SAAS && profile.legal?.accepted_at && (
                <div data-testid="legal-accepted" className={`${HINT} mt-1`}>
                  {/* Ссылки — как в «О сервисе»: серым их не узнавали (тест владельца 25.09). */}
                  <a href="/oferta" target="_blank" rel="noopener noreferrer" className={DOC_LINK}>Оферта</a>
                  {' и '}
                  <a href="/consent" target="_blank" rel="noopener noreferrer" className={DOC_LINK}>согласие на обработку персональных данных</a>
                  {` приняты ${formatMoscowDate(profile.legal.accepted_at)}, редакция от ${formatMoscowDate(profile.legal.offer)}`}
                </div>
              )}
            </div>

            <div>
              <div className={LABEL}>Как к вам обращаться</div>
              <div className="mt-2 flex gap-2">
                <input
                  className={FIELD}
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Не указано"
                />
                <button
                  type="button"
                  className={`${BTN} shrink-0 bg-paper-ink text-white disabled:bg-paper-bg disabled:text-paper-hint`}
                  onClick={handleSaveDisplayName}
                  disabled={saving || displayName === (profile.display_name || '')}
                >
                  {saving ? '…' : 'Сохранить'}
                </button>
              </div>
              {saveMsg && (
                <div className={`text-[12px] mt-2 ${saveMsg.type === 'error' ? 'text-paper-accent' : 'text-paper-success'}`}>
                  {saveMsg.text}
                </div>
              )}
            </div>

          </section>

          <section className={CARD}>
            <div className="font-display text-[20px] font-extrabold tracking-[-0.03em] text-paper-ink">
              История и данные
            </div>

            <label className="flex items-start justify-between gap-3 cursor-pointer">
              <span>
                <span className={VALUE}>Хранить расшифровки и протоколы</span>
                <span className={`${HINT} block mt-1`}>
                  Видны только вам. Выключение не удаляет сохранённое.
                </span>
              </span>
              <input type="checkbox" className="mt-1 w-4 h-4 accent-paper-accent shrink-0"
                     checked={histEnabled} disabled={histSaving}
                     onChange={(e) => saveHist({ history_enabled: e.target.checked })} />
            </label>

            {histEnabled && (
              <div>
                <div className={LABEL}>Срок хранения новых записей</div>
                <select className={`${FIELD} mt-2`} value={histRetention} disabled={histSaving}
                        onChange={(e) => saveHist({ history_retention: e.target.value })}>
                  <option value="week">Неделя</option>
                  <option value="month">Месяц</option>
                  <option value="year">Год</option>
                  <option value="forever">Бессрочно</option>
                </select>
                <div className={`${HINT} mt-2`}>
                  {retentionNote
                    ? 'Новый срок применяется только к новым записям; уже сохранённые не удаляются.'
                    : 'Уже сохранённые записи срок не меняет.'}
                </div>
              </div>
            )}

            {/* Показывается всегда. Не прятать за audio_extract_enabled из токена:
                bit-auth гасит этот флаг, когда человек снял галочку, и после
                следующего входа включить её обратно было бы нечем (так было
                с 11.08 по 23.09). */}
            <label className="flex items-start justify-between gap-3 cursor-pointer border-t border-paper-line pt-4">
              <span>
                {/* Пояснение — подсказкой при наведении: абзацем оно было длиннее
                    самой настройки (тест владельца 25.09). */}
                <span className={`${VALUE} inline-flex items-center gap-1`}>
                  Извлекать звук из видео до загрузки
                  <Info className="w-3.5 h-3.5 text-paper-ink-3 shrink-0" data-testid="audio-extract-hint"
                        role="img" aria-label={AUDIO_EXTRACT_HINT}
                        onClick={(e) => e.preventDefault()}>
                    <title>{AUDIO_EXTRACT_HINT}</title>
                  </Info>
                </span>
              </span>
              <input type="checkbox" className="mt-1 w-4 h-4 accent-paper-accent shrink-0"
                     checked={audioExtractOptIn} disabled={audioExtractSaving}
                     onChange={(e) => saveAudioExtractOptIn(e.target.checked)} />
            </label>

            <div className="border-t border-paper-line pt-4">
              <button type="button" className={BTN_DANGER} onClick={() => setConfirmWipe(true)}>
                Удалить всю историю
              </button>
            </div>
          </section>

          {voiceEnabled && (
            <section className={CARD}>
              <div>
                <div className="font-display text-[20px] font-extrabold tracking-[-0.03em] text-paper-ink">
                  Запомненные голоса
                </div>
                <div className={`${HINT} mt-1`}>
                  Участники, которых вы попросили запомнить: в новых записях подписываем
                  автоматически. Хранится только числовой отпечаток, не запись разговора.
                  Удаление — сразу и без следа.
                </div>
              </div>
              {voiceError && <div className="text-[12px] text-paper-accent">{voiceError}</div>}
              {voices.length === 0 ? (
                <div className={HINT}>
                  Пока ни одного. Отметьте «запомнить голос» при переименовании участников
                  в расшифровке.
                </div>
              ) : (
                <ul className="flex flex-col">
                  {voices.map((voice) => (
                    <li key={voice.id}
                        className="flex items-center justify-between gap-3 py-3 border-t border-paper-line first:border-0 first:pt-0">
                      <span>
                        <span className={VALUE}>{voice.name}</span>
                        <span className={`${HINT} block`}>записей голоса: {voice.samples}</span>
                      </span>
                      <button type="button" className={BTN_DANGER} disabled={voiceBusy}
                              onClick={() => removeVoice(voice.id)}>
                        Удалить
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          <AboutService />
        </div>
      </div>

      <Dialog open={showRegenerate} onOpenChange={setShowRegenerate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Обновить токен?</DialogTitle>
            <DialogDescription>
              Текущий токен станет недействительным. Новый токен будет отправлен на ваш email.
              Вам потребуется заново войти в систему.
            </DialogDescription>
          </DialogHeader>
          {regenerateError && (
            <div className="rounded-panel bg-paper-accent/[0.06] text-paper-accent p-3 text-[13px]">
              {regenerateError}
            </div>
          )}
          <DialogFooter>
            <button type="button" className={BTN_QUIET} onClick={() => setShowRegenerate(false)} disabled={regenerating}>
              Отмена
            </button>
            <button
              type="button"
              className={`${BTN} bg-paper-accent text-white hover:bg-paper-accent-hover`}
              onClick={handleRegenerate}
              disabled={regenerating}
            >
              {regenerating ? 'Обновление…' : 'Обновить'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmWipe} onOpenChange={setConfirmWipe}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Удалить всю историю?</DialogTitle>
            <DialogDescription>Все сохранённые расшифровки и протоколы будут удалены безвозвратно.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button type="button" className={BTN_QUIET} onClick={() => setConfirmWipe(false)}>Отмена</button>
            <button type="button" className={`${BTN} bg-paper-accent text-white hover:bg-paper-accent-hover`}
                    onClick={wipeHistory}>
              Удалить
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
